'use client';

import * as React from 'react';
import Link from 'next/link';
import { ArrowUpRight, Search } from 'lucide-react';

import { MCP_TOOL_COUNT, MCP_TOOL_GROUPS } from '~/lib/mcp-catalog';

const EFFECT_LABELS = {
  read: 'Read / inspect',
  write: 'Workspace change',
  credits: 'May use credits',
  outreach: 'May trigger outreach',
  connect: 'Connection setup'
} as const;

export function McpCapabilities(): React.JSX.Element {
  const [query, setQuery] = React.useState('');
  const [groupId, setGroupId] = React.useState('all');
  const normalized = query.trim().toLowerCase();
  const groups = MCP_TOOL_GROUPS.filter(
    (group) => groupId === 'all' || group.id === groupId
  )
    .map((group) => ({
      ...group,
      tools: group.tools.filter((tool) =>
        `${group.title} ${group.description} ${tool.name} ${tool.description}`
          .toLowerCase()
          .includes(normalized)
      )
    }))
    .filter((group) => group.tools.length > 0);
  const resultCount = groups.reduce(
    (count, group) => count + group.tools.length,
    0
  );

  return (
    <section
      id="capabilities"
      aria-labelledby="capabilities-heading"
      className="scroll-mt-24 bg-[color:var(--paper)] py-20 md:py-28"
    >
      <div className="mx-auto max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16">
        <div className="grid gap-6 lg:grid-cols-[1fr_0.8fr] lg:items-end lg:gap-16">
          <div>
            <p className="eyebrow mb-3">The complete toolkit</p>
            <h2
              id="capabilities-heading"
              className="display-h2 text-balance text-3xl text-[color:var(--ink)] md:text-5xl"
            >
              {MCP_TOOL_COUNT} functions.
              <br />
              <span className="text-[color:var(--ink-3)]">
                From question to next step.
              </span>
            </h2>
          </div>
          <div>
            <p className="max-w-xl text-base leading-relaxed text-[color:var(--ink-3)]">
              Every tool your assistant can call, grouped by the work it helps
              you do. Explore the capabilities here, then open the reference for
              inputs, results and important limits.
            </p>
            <Link
              href="/docs/lumnis-mcp"
              className="mt-4 inline-flex min-h-11 items-center gap-2 text-sm font-medium text-[color:var(--accent-darker)] underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-700"
            >
              Read the complete function reference{' '}
              <ArrowUpRight
                aria-hidden
                className="size-4"
              />
            </Link>
          </div>
        </div>
        <div className="mt-10 grid gap-4 rounded-xl bg-[color:var(--paper-2)] p-4 sm:grid-cols-[1fr_auto] md:p-5">
          <div>
            <label
              htmlFor="mcp-tool-search"
              className="mb-2 block text-xs font-medium text-[color:var(--ink-2)]"
            >
              Find a capability or function
            </label>
            <div className="relative">
              <Search
                aria-hidden
                className="absolute left-3 top-3.5 size-4 text-[color:var(--ink-3)]"
              />
              <input
                id="mcp-tool-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Try person intelligence, email or playbook"
                className="h-11 w-full rounded-md border border-[color:var(--hairline)] bg-[color:var(--paper)] pl-10 pr-3 text-sm text-[color:var(--ink)] placeholder:text-[color:var(--ink-3)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="mcp-tool-group"
              className="mb-2 block text-xs font-medium text-[color:var(--ink-2)]"
            >
              Workflow
            </label>
            <select
              id="mcp-tool-group"
              value={groupId}
              onChange={(event) => setGroupId(event.target.value)}
              className="h-11 w-full rounded-md border border-[color:var(--hairline)] bg-[color:var(--paper)] px-3 text-sm text-[color:var(--ink)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
            >
              <option value="all">All workflows</option>
              {MCP_TOOL_GROUPS.map((group) => (
                <option
                  key={group.id}
                  value={group.id}
                >
                  {group.title}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="my-5 font-mono text-xs text-[color:var(--ink-3)]"
        >
          {resultCount} of {MCP_TOOL_COUNT} functions
        </p>
        <div className="divide-y divide-[color:var(--hairline)] border-y border-[color:var(--hairline)]">
          {groups.map((group) => (
            <section
              key={group.id}
              aria-labelledby={`mcp-group-${group.id}`}
              className="grid gap-6 py-8 lg:grid-cols-[0.7fr_1.8fr] lg:gap-14"
            >
              <div>
                <h3
                  id={`mcp-group-${group.id}`}
                  className="text-lg font-semibold text-[color:var(--ink)]"
                >
                  {group.title}
                </h3>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-[color:var(--ink-3)]">
                  {group.description}
                </p>
              </div>
              <ul className="divide-y divide-[color:var(--hairline-2)]">
                {group.tools.map((tool) => (
                  <li
                    key={tool.name}
                    className="py-4 first:pt-0 last:pb-0"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                      <Link
                        href={`/docs/lumnis-mcp#${tool.name}`}
                        className="inline-flex min-h-8 min-w-0 items-center gap-1.5 font-mono text-[13px] font-medium text-[color:var(--ink)] underline-offset-4 hover:text-[color:var(--accent-darker)] hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-700"
                      >
                        <span className="break-all">{tool.name}</span>
                        <ArrowUpRight
                          aria-hidden
                          className="size-3.5 shrink-0"
                        />
                      </Link>
                      <span
                        className={`rounded px-2 py-1 text-[10px] font-medium ${tool.effect === 'outreach' || tool.effect === 'credits' ? 'bg-[color:var(--mint)] text-[color:var(--accent-darker)]' : 'bg-[color:var(--paper-2)] text-[color:var(--ink-3)]'}`}
                      >
                        {EFFECT_LABELS[tool.effect]}
                      </span>
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-[color:var(--ink-3)]">
                      {tool.description}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {resultCount === 0 && (
            <div className="py-12 text-center">
              <h3 className="text-lg font-semibold text-[color:var(--ink)]">
                No matching functions
              </h3>
              <p className="mt-2 text-sm text-[color:var(--ink-3)]">
                Try a broader term or another workflow.
              </p>
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setGroupId('all');
                }}
                className="mt-4 min-h-11 px-4 text-sm font-medium text-[color:var(--accent-darker)] underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-700"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
        <aside
          aria-labelledby="mcp-action-boundaries"
          className="mt-10 grid gap-5 rounded-xl bg-[color:var(--paper-2)] p-6 lg:grid-cols-[0.7fr_1.8fr] lg:gap-14"
        >
          <h3
            id="mcp-action-boundaries"
            className="text-lg font-semibold text-[color:var(--ink)]"
          >
            Know what a call can do.
          </h3>
          <div className="space-y-3 text-sm leading-relaxed text-[color:var(--ink-3)]">
            <p>
              Read existing research first. Fresh searches, research and contact
              lookups can spend credits. Email is the default contact lookup;
              phone is opt-in. New enrichment results are returned, not saved to
              the project.
            </p>
            <p>
              Campaign launch always starts; set approval settings in the same
              call. Adding people to an active campaign or approving an
              action can trigger outreach; approval settings can also allow
              automatic actions.
            </p>
            <p>
              These labels describe the main effect, not a permission guarantee.
              Some reads sync background work. If a call times out, preserve its
              results and follow its recovery instructions before retrying.
              Provider sign-in and some recovery steps finish in the browser or
              dashboard.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}
